/**
 * Bouton qui s'active/désactive selon les permissions.
 */
import React from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { PermissionCode, PlatformRoleName, OperationalRoleName } from '@/types/permissions';

interface PermissionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  permission?: PermissionCode;
  permissions?: PermissionCode[];
  role?: PlatformRoleName | OperationalRoleName;
  roles?: (PlatformRoleName | OperationalRoleName)[];
  requireAll?: boolean;
  disabledMessage?: string;
  children: React.ReactNode;
}

const PermissionButton: React.FC<PermissionButtonProps> = ({
  permission,
  permissions = [],
  role,
  roles = [],
  requireAll = false,
  disabledMessage = "Permission insuffisante",
  children,
  className = '',
  disabled,
  ...props
}) => {
  const { 
    hasPermission, 
    hasAnyPermission, 
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    loading 
  } = usePermissions();

  // Si en cours de chargement, désactiver le bouton
  if (loading) {
    return (
      <button 
        {...props} 
        disabled 
        className={`${className} opacity-50 cursor-not-allowed`}
        title="Chargement des permissions..."
      >
        {children}
      </button>
    );
  }

  let hasAccess = false;

  // Vérifier les permissions
  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions.length > 0) {
    hasAccess = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
  }

  // Vérifier les rôles
  if (role) {
    hasAccess = hasRole(role);
  } else if (roles.length > 0) {
    hasAccess = requireAll 
      ? hasAllRoles(roles)
      : hasAnyRole(roles);
  }

  // Si pas de permission/rôle spécifié, autoriser l'accès
  if (!permission && permissions.length === 0 && !role && roles.length === 0) {
    hasAccess = true;
  }

  const isDisabled = disabled || !hasAccess;
  const title = isDisabled && !disabled ? disabledMessage : props.title;

  return (
    <button 
      {...props} 
      disabled={isDisabled}
      className={`${className} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      title={title}
    >
      {children}
    </button>
  );
};

export default PermissionButton;
